import type { EntityType } from '@/shared/types/company.types';

export interface EntityTab {
  id: string;
  label: string;
  icon: string;
}

export interface EntityConfig {
  entityType: EntityType;
  label: string;
  icon: string;
  accent: 'teal' | 'orange' | 'blue' | 'indigo' | 'emerald' | 'amber';
  routePath: string;
  description: string;
  tabs: EntityTab[];
}

const BASE_TABS: EntityTab[] = [
  { id: 'accueil', label: 'Accueil', icon: 'ri-home-line' },
  { id: 'commandes', label: 'Commandes', icon: 'ri-truck-line' },
  { id: 'profil', label: 'Profil', icon: 'ri-building-line' },
  { id: 'finances', label: 'Finances', icon: 'ri-wallet-3-line' },
];

export const ENTITY_CONFIGS: Record<EntityType, EntityConfig> = {
  restaurant: {
    entityType: 'restaurant',
    label: 'Restaurant',
    icon: 'ri-restaurant-line',
    accent: 'orange',
    routePath: '/dashboard/restaurant',
    description: 'Livraisons repas chauds et clients réguliers',
    tabs: [
      ...BASE_TABS.slice(0, 2),
      { id: 'clients', label: 'Clients', icon: 'ri-group-line' },
      ...BASE_TABS.slice(2),
    ],
  },
  commercant: {
    entityType: 'commercant',
    label: 'Commerçant',
    icon: 'ri-store-2-line',
    accent: 'teal',
    routePath: '/dashboard/commercant',
    description: 'Boutique et livraisons locales',
    tabs: BASE_TABS,
  },
  artisan: {
    entityType: 'artisan',
    label: 'Artisan',
    icon: 'ri-hammer-line',
    accent: 'amber',
    routePath: '/dashboard/artisan',
    description: 'Livraison de créations et commandes sur mesure',
    tabs: BASE_TABS,
  },
  ecommerce: {
    entityType: 'ecommerce',
    label: 'E-commerce',
    icon: 'ri-shopping-bag-line',
    accent: 'blue',
    routePath: '/dashboard/ecommerce',
    description: 'Gestion des colis et expéditions en ligne',
    tabs: [
      ...BASE_TABS.slice(0, 2),
      { id: 'stock', label: 'Stock', icon: 'ri-archive-line' },
      ...BASE_TABS.slice(2),
    ],
  },
  pme: {
    entityType: 'pme',
    label: 'PME',
    icon: 'ri-building-2-line',
    accent: 'indigo',
    routePath: '/dashboard/pme',
    description: 'Logistique pour petites et moyennes entreprises',
    tabs: BASE_TABS,
  },
  'grande-entreprise': {
    entityType: 'grande-entreprise',
    label: 'Grande Entreprise',
    icon: 'ri-building-4-line',
    accent: 'indigo',
    routePath: '/dashboard/grande-entreprise',
    description: 'Flux logistiques à grande échelle',
    tabs: [
      ...BASE_TABS.slice(0, 2),
      { id: 'equipe', label: 'Équipe', icon: 'ri-team-line' },
      ...BASE_TABS.slice(2),
    ],
  },
  demenagement: {
    entityType: 'demenagement',
    label: 'Déménagement',
    icon: 'ri-truck-line',
    accent: 'amber',
    routePath: '/dashboard/demenagement',
    description: 'Devis et transports de mobilier',
    tabs: [
      ...BASE_TABS.slice(0, 2),
      { id: 'devis', label: 'Devis', icon: 'ri-file-list-line' },
      ...BASE_TABS.slice(2),
    ],
  },
  'partenaire-logistique': {
    entityType: 'partenaire-logistique',
    label: 'Partenaire Logistique',
    icon: 'ri-route-line',
    accent: 'blue',
    routePath: '/dashboard/partenaire-logistique',
    description: 'Réseau de transport et convois',
    tabs: BASE_TABS,
  },
  'partenaire-financier': {
    entityType: 'partenaire-financier',
    label: 'Partenaire Financier',
    icon: 'ri-bank-line',
    accent: 'emerald',
    routePath: '/dashboard/partenaire-financier',
    description: 'Mobile Money, COD et paiements',
    tabs: [
      ...BASE_TABS.slice(0, 2),
      { id: 'transactions', label: 'Transactions', icon: 'ri-exchange-line' },
      ...BASE_TABS.slice(2),
    ],
  },
  'point-relais': {
    entityType: 'point-relais',
    label: 'Point Relais',
    icon: 'ri-map-pin-user-line',
    accent: 'teal',
    routePath: '/dashboard/point-relais',
    description: 'Réception et remise de colis',
    tabs: [
      ...BASE_TABS.slice(0, 2),
      { id: 'stockage', label: 'Stockage', icon: 'ri-inbox-archive-line' },
      ...BASE_TABS.slice(2),
    ],
  },
  administration: {
    entityType: 'administration',
    label: 'Administration Publique',
    icon: 'ri-government-line',
    accent: 'indigo',
    routePath: '/dashboard/administration',
    description: 'Services publics et courriers officiels',
    tabs: BASE_TABS,
  },
  'organisation-internationale': {
    entityType: 'organisation-internationale',
    label: 'Organisation Internationale',
    icon: 'ri-global-line',
    accent: 'blue',
    routePath: '/dashboard/organisation-internationale',
    description: 'Missions humanitaires et logistique internationale',
    tabs: BASE_TABS,
  },
  ong: {
    entityType: 'ong',
    label: 'ONG',
    icon: 'ri-heart-line',
    accent: 'emerald',
    routePath: '/dashboard/ong',
    description: 'Aide humanitaire et distributions',
    tabs: [
      ...BASE_TABS.slice(0, 2),
      { id: 'missions', label: 'Missions', icon: 'ri-hand-heart-line' },
      ...BASE_TABS.slice(2),
    ],
  },
  'health-professional': {
    entityType: 'health-professional',
    label: 'Santé',
    icon: 'ri-capsule-line',
    accent: 'emerald',
    routePath: '/dashboard/health-professional',
    description: 'Livraison médicaments et produits de santé',
    tabs: BASE_TABS,
  },
  'educational-institution': {
    entityType: 'educational-institution',
    label: 'Établissement Scolaire',
    icon: 'ri-school-line',
    accent: 'blue',
    routePath: '/dashboard/educational-institution',
    description: 'Documents, fournitures et repas scolaires',
    tabs: BASE_TABS,
  },
  'event-organizer': {
    entityType: 'event-organizer',
    label: 'Organisateur d\'Événements',
    icon: 'ri-calendar-event-line',
    accent: 'orange',
    routePath: '/dashboard/event-organizer',
    description: 'Logistique événementielle',
    tabs: BASE_TABS,
  },
};

export const ENTITY_ROUTE_TO_TYPE: Record<string, EntityType> = Object.fromEntries(
  Object.values(ENTITY_CONFIGS).map((c) => [c.routePath, c.entityType]),
) as Record<string, EntityType>;

export function getEntityConfig(type: EntityType): EntityConfig {
  return ENTITY_CONFIGS[type];
}

export function resolveEntityDashboardPath(entityType: EntityType): string {
  return ENTITY_CONFIGS[entityType]?.routePath ?? '/dashboard/company';
}
